import React, { useEffect } from "react";
import { ChevronRight } from "lucide-react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  CheckBadgeIcon,
  PencilSquareIcon,
  DocumentChartBarIcon,
  DocumentIcon,
  ShareIcon,
} from "@heroicons/react/24/outline";
import {
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar";
import { Collapsible } from "../ui/collapsible";
import { featureFlags } from "@/utils/featureFlags";
import { TeamSwitcher } from "./team-switcher";
import useWindowDimensions from "@/hooks/useWindowDimensions";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import useUnsavedChangesStore from "@/utils/UnsavedChangesStore";
import { useNavigate } from "react-router-dom";

interface NavItem {
  title: string;
  route?: string;
  icon?: React.ComponentType<{ className?: string }>;
  variant?: "default" | "ghost";
  children?: NavItem[];
}

export function AppSidebar({
  isCollapsed,
  sideOpen,
}: {
  isCollapsed: boolean;
  sideOpen: (open: boolean) => void;
}) {
  const { noteId = "", folder = "" } = useParams<{
    folder: string;
    noteId: string;
  }>();

  const { templateId = "" } = useParams<{
    templateId: string;
  }>();

  const { height, width } = useWindowDimensions();
  const location = useLocation();

  const { setOpen, open } = useSidebar();

  const navigate = useNavigate();
  const { setPendingNavigation } = useUnsavedChangesStore();

  const isAdmin = useIsAdmin();

  const ROUTES: NavItem[] = React.useMemo(
    () => [
      {
        title: "Notes",
        route: "/dashboard/notes",
        icon: PencilSquareIcon,
        variant: "default",
      },
      {
        title: "Signed",
        route: "/dashboard/signed",
        icon: CheckBadgeIcon,
        variant: "ghost",
      },
      ...(featureFlags.templates
        ? [
            {
              title: "Templates",
              icon: DocumentChartBarIcon,
              children: [
                {
                  title: "My Templates",
                  route: "/dashboard/templates",
                  icon: DocumentIcon,
                },
                {
                  title: "Shared With Me",
                  route: "/dashboard/shared-templates",
                  icon: ShareIcon,
                },
              ],
            },
          ]
        : []),
    ],
    []
  );

  const ADMIN_ROUTES: NavItem[] = React.useMemo(
    () => [
      ...(featureFlags.templates
        ? [
            {
              title: "Templates",
              icon: DocumentChartBarIcon,
              route: "/admin/dashboard/templates",
              children: [],
            },
          ]
        : []),
    ],
    []
  );

  useEffect(() => {
    sideOpen(open);
  }, [open]);

  const handleNavigation = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    route: string
  ): void => {
    event.preventDefault();
    if (templateId) {
      setPendingNavigation(route);
    } else {
      navigate(route);
    }
  };

  useEffect(() => {
    setOpen(
      width <= 1024
        ? false
        : (noteId || templateId) && width >= 768
        ? false
        : true
    );
  }, [noteId, templateId, width]);

  const renderNavItem = React.useCallback(
    (item: NavItem, index: number) => {
      if (item.children?.length) {
        return (
          <Collapsible
            key={item.title}
            defaultOpen={index === 1}
            className={
              "group/collapsible " +
              (location.pathname.startsWith(item.route!) ? "text-white" : "")
            }
          >
            <SidebarMenuItem>
              <CollapsibleTrigger asChild onClick={() => setOpen(true)}>
                <SidebarMenuButton>
                  <div
                    className={
                      "flex items-center hover:text-white " +
                      (location.pathname.startsWith(item.route!)
                        ? "text-white"
                        : "")
                    }
                  >
                    {item.icon && <item.icon className="mr-2 h-5 w-5" />}
                    {item.title}
                  </div>

                  <ChevronRight className="h-4 w-4 ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                </SidebarMenuButton>
              </CollapsibleTrigger>

              <CollapsibleContent>
                <SidebarMenuSub>
                  {item.children.map((child) => (
                    <SidebarMenuSubItem key={child.title}>
                      <SidebarMenuSubButton
                        asChild
                        isActive={location.pathname.endsWith(child.route!)}
                      >
                        <Link
                          onClick={(event) =>
                            handleNavigation(event, child.route!)
                          }
                          to={child.route!}
                          className={
                            "hover:text-white " +
                            (location.pathname.startsWith(item.route!)
                              ? "text-white"
                              : "")
                          }
                        >
                          <div
                            className={
                              "flex items-center hover:text-white " +
                              (location.pathname.startsWith(item.route!)
                                ? "text-white"
                                : "")
                            }
                          >
                            {child.icon && (
                              <child.icon className="mr-2 h-[18px] w-[18px]" />
                            )}
                            {child.title}
                          </div>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  ))}
                </SidebarMenuSub>
              </CollapsibleContent>
            </SidebarMenuItem>
          </Collapsible>
        );
      }

      return (
        <SidebarMenuItem
          key={item.title}
          className={
            "hover:text-white " +
            (location.pathname.startsWith(item.route!) ? "text-white" : "")
          }
        >
          <SidebarMenuButton
            asChild
            isActive={location.pathname.startsWith(item.route!)}
          >
            <Link
              onClick={(event) => handleNavigation(event, item.route!)}
              to={item.route!}
              className={
                "hover:text-white " +
                (location.pathname.startsWith(item.route!) ? "text-white" : "")
              }
            >
              <div
                className={
                  "flex hover:text-white " +
                  (location.pathname.startsWith(item.route!)
                    ? "text-white"
                    : "")
                }
              >
                {item.icon && <item.icon className="mr-2 h-5 w-5" />}
                {item.title}
              </div>
            </Link>
          </SidebarMenuButton>
        </SidebarMenuItem>
      );
    },
    [location.pathname]
  );

  return (
    <Sidebar collapsible={"icon"} className="">
      <SidebarHeader className="h-[65px]">
        <TeamSwitcher
          teams={[
            ...(isAdmin
              ? [
                  {
                    name: "Kortex.ai",
                    text: "Kortex.ai",
                    plan: "",
                    route: "/dashboard/notes",
                  },
                ]
              : [
                  {
                    name: "Kortex.ai",
                    text: "Admin",
                    plan: "Admin",
                    route: "/admin/dashboard/templates",
                  },
                ]),
          ]}
        />
      </SidebarHeader>
      <SidebarContent>
        <SidebarMenu className="px-2">
          {(isAdmin ? ADMIN_ROUTES : ROUTES).map((item, index) =>
            renderNavItem(item, index)
          )}
        </SidebarMenu>
      </SidebarContent>
      <SidebarRail />
    </Sidebar>
  );
}
